import { apolloClient } from './apollo-client'
import { gql } from '@apollo/client'

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String) {
    refreshToken(refreshToken: $refreshToken) {
      user {
        id
        xUsername
      }
      token
      refreshToken
    }
  }
`

let tokenExpiryTime: number | null = null
let refreshTimer: number | null = null
let isRefreshing = false

/**
 * Decode JWT token to get expiration time
 */
function decodeToken(token: string): { exp: number } | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Failed to decode token:', error)
    return null
  }
}

/**
 * Calculate when to refresh token (80% of lifetime)
 */
function calculateRefreshTime(expiresAt: number): number {
  const now = Date.now()
  const lifetime = expiresAt - now

  // Refresh at 80% of lifetime, or in 1 hour if lifetime is very long
  const refreshIn = Math.min(lifetime * 0.8, 60 * 60 * 1000)

  return now + refreshIn
}

/**
 * Refresh the access token using refresh token
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (isRefreshing) {
    console.log('🔄 Token refresh already in progress, skipping...')
    return false
  }

  try {
    isRefreshing = true
    console.log('🔄 Refreshing access token using httpOnly cookie...')

    // Don't pass refreshToken - let backend read from httpOnly cookie
    // This is more secure and ensures cookies are properly used
    const { data, errors } = await apolloClient.mutate({
      mutation: REFRESH_TOKEN_MUTATION,
      variables: {
        refreshToken: undefined // Backend will read from httpOnly cookie
      },
      // Don't use cache for token refresh
      fetchPolicy: 'no-cache',
      context: {
        credentials: 'include' // Ensure cookies are sent with request
      }
    })

    if (errors || !data?.refreshToken) {
      console.error('❌ Token refresh failed:', errors)
      // Clear tokens and trigger re-auth
      localStorage.removeItem('x11-auth-token')
      localStorage.removeItem('x11-refresh-token')
      document.cookie = 'x11-auth-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
      document.cookie = 'x11-refresh-token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'

      // Dispatch event to notify auth provider
      window.dispatchEvent(new CustomEvent('authTokenExpired'))
      return false
    }

    const { token, refreshToken: newRefreshToken } = data.refreshToken

    // Update localStorage for backward compatibility (extensions, etc.)
    if (token) {
      localStorage.setItem('x11-auth-token', token)
      window.dispatchEvent(new CustomEvent('authTokenChanged'))
    }
    if (newRefreshToken) {
      localStorage.setItem('x11-refresh-token', newRefreshToken)
    }

    // Calculate new expiry time
    const decoded = decodeToken(token)
    if (decoded?.exp) {
      tokenExpiryTime = decoded.exp * 1000 // Convert to ms
      scheduleTokenRefresh()
    }

    console.log('✅ Token refreshed successfully')
    return true
  } catch (error) {
    if (/No refresh token/i.test(String((error as any)?.message ?? error))) {
      console.info('No login cookie: signed out')
    } else {
      console.error('❌ Error refreshing token:', error)
    }

    // Clear tokens on error
    localStorage.removeItem('x11-auth-token')
    localStorage.removeItem('x11-refresh-token')
    window.dispatchEvent(new CustomEvent('authTokenExpired'))
    return false
  } finally {
    isRefreshing = false
  }
}

/**
 * Schedule the next token refresh
 */
function scheduleTokenRefresh() {
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }

  if (!tokenExpiryTime) {
    return
  }

  const refreshTime = calculateRefreshTime(tokenExpiryTime)
  const delay = refreshTime - Date.now()

  if (delay <= 0) {
    // Token already expired or about to expire, refresh immediately
    console.log('⚠️ Token expired or expiring soon, refreshing immediately')
    refreshAccessToken()
    return
  }

  console.log(`⏰ Token refresh scheduled in ${Math.round(delay / 1000 / 60)} minutes`)

  refreshTimer = window.setTimeout(() => {
    refreshAccessToken()
  }, delay) as unknown as number
}

/**
 * Initialize token refresh mechanism
 */
export function initializeTokenRefresh() {
  // Check if we have a token
  const token = localStorage.getItem('x11-auth-token')

  if (!token) {
    console.log('No token found, skipping token refresh initialization')
    return
  }

  // Decode token to get expiry
  const decoded = decodeToken(token)
  if (decoded?.exp) {
    tokenExpiryTime = decoded.exp * 1000
    scheduleTokenRefresh()
  }

  // Also check token validity every 15 minutes
  const intervalCheck = setInterval(() => {
    const currentToken = localStorage.getItem('x11-auth-token')
    if (!currentToken) {
      clearInterval(intervalCheck)
      return
    }

    const decodedToken = decodeToken(currentToken)
    if (decodedToken?.exp) {
      const expiryTime = decodedToken.exp * 1000
      const now = Date.now()

      // If token expires in less than 10 minutes, refresh now
      if (expiryTime - now < 10 * 60 * 1000) {
        console.log('⚠️ Token expiring soon (< 10 min), refreshing...')
        refreshAccessToken()
      }
    }
  }, 15 * 60 * 1000) // Check every 15 minutes

  // Listen for storage events (token changes from other tabs)
  window.addEventListener('storage', (e) => {
    if (e.key === 'x11-auth-token' && e.newValue) {
      const decoded = decodeToken(e.newValue)
      if (decoded?.exp) {
        tokenExpiryTime = decoded.exp * 1000
        scheduleTokenRefresh()
      }
    }
  })

  // Listen for custom token change events (same tab)
  window.addEventListener('authTokenChanged', () => {
    const token = localStorage.getItem('x11-auth-token')
    if (token) {
      const decoded = decodeToken(token)
      if (decoded?.exp) {
        tokenExpiryTime = decoded.exp * 1000
        scheduleTokenRefresh()
      }
    }
  })

  // Listen for page visibility changes (refresh when coming back)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && tokenExpiryTime) {
      const now = Date.now()
      // If token expires in less than 5 minutes, refresh immediately
      if (tokenExpiryTime - now < 5 * 60 * 1000) {
        console.log('⚠️ Page became visible, token expiring soon, refreshing...')
        refreshAccessToken()
      }
    }
  })

  console.log('✅ Token refresh mechanism initialized')
}

/**
 * Cleanup token refresh (for unmounting)
 */
export function cleanupTokenRefresh() {
  if (refreshTimer) {
    window.clearTimeout(refreshTimer)
    refreshTimer = null
  }
  tokenExpiryTime = null
}
